import { useEffect, useRef } from 'react';
import {
    FinancingApi,
    LoanApplication,
    SubmitApplicationResult,
} from '@servicetitan/financing-api-client';
import {
    PrequalsApi,
    PrequalApplication,
    SubmitPrequalResponseDto,
    LoanApplicationSubmitSource,
} from '@servicetitan/financing-api-client/src/prequals-api';
import {
    ApplicationDataStore,
    DisclosureDataStore,
    DynamicDisclosureStore,
    prepareApplicationData,
    SubmittingUserStore,
} from '@servicetitan/financing-application-shared';
import { FinancedAmountStore, SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useMutation } from '@tanstack/react-query';
import {
    SetupDataStore,
    useProviderConfig,
    PrequalificationResponseStore,
} from '@servicetitan/financing-workflow-shared';
import { DecisionDataStore } from '@servicetitan/financing-decision-shared';
import { ServerError } from '@servicetitan/financing-utils-shared';
import { preparePrequalificationData } from '../../prequal-application/preparePrequalificationData';

// Module-level fingerprint persistence to survive hook re-initializations
let persistedFingerprint: string | undefined;

export interface UseLoanSubmissionOptions {
    /** The prequalification ID to submit (optional - will use setupData.prequalificationId if not provided for prequal submissions) */
    prequalId?: string;
    /** Whether to automatically submit on mount (default: true) */
    autoSubmit?: boolean;
    /** Whether this hook is enabled (default: true) */
    enabled?: boolean;
    /** GreenSky fingerprint (if applicable) */
    fingerprint?: string;
    /** Custom success callback - if provided, overrides default workflow stage transition */
    onSuccess?: (data: { data: SubmitApplicationResult | SubmitPrequalResponseDto }) => void;
    /** Custom error callback */
    onError?: (error: ServerError) => void;
}

export interface UseLoanSubmissionReturn {
    /** Submit the loan application manually */
    submitLoan: () => void;
    /** Whether the loan is currently being submitted */
    isSubmitting: boolean;
    /** Whether the loan has been submitted successfully */
    isSuccess: boolean;
    /** Any error that occurred during submission */

    error: ServerError | null;
    /** Whether this is a GreenSky provider */
    isGreenSky: boolean;
    /** GreenSky fingerprint (if applicable) */
    fingerprint: string | undefined;
    /** Reset the submission state */
    reset: () => void;
    /** Prequal ID from submission response (for prequal submissions) */
    prequalId: string | null;
    /** Loan ID from submission response (for financing submissions) */
    loanId: string | null;
}

export const useLoanSubmission = (
    options: UseLoanSubmissionOptions = {},
): UseLoanSubmissionReturn => {
    const {
        prequalId,
        autoSubmit = true,
        enabled = true,
        fingerprint: passedFingerprint,
        onSuccess,
        onError,
    } = options;
    const providerConfig = useProviderConfig();
    const { setupData } = SetupDataStore.useStore();

    // Track submission state to prevent double submission in strict mode
    const hasSubmittedRef = useRef(false);

    // GoodLeap always uses financing API, never prequals API
    const isGoodLeap = setupData?.provider?.toLowerCase() === 'goodleap';
    const usePrequalSubmission = isGoodLeap
        ? false
        : providerConfig.useFinancingSubmission === false;

    const [financingApi] = useDependencies(FinancingApi);
    const [prequalsApi] = useDependencies(PrequalsApi);

    const { applicationData } = ApplicationDataStore.useStore();
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { saleAmount, downPayment, financedAmount } = FinancedAmountStore.useStore();
    const { setDecisionData } = DecisionDataStore.useStore();
    const { submittingUser } = SubmittingUserStore.useStore();
    const { setPrequalificationResponse } = PrequalificationResponseStore.useStore();
    const { hasMailingAddress } = useProviderConfig();

    // GreenSky Iovation (used by both submission types)
    const isGreenSky = setupData?.provider?.toLowerCase() === 'greensky';
    const fingerprint = persistedFingerprint ?? passedFingerprint;

    // Update persisted fingerprint when a new one is captured
    if (isGreenSky && passedFingerprint && !persistedFingerprint) {
        persistedFingerprint = passedFingerprint;
    }

    const { disclosureState } = DisclosureDataStore.useStore();
    const { dynamicDisclosureState } = DynamicDisclosureStore.useStore();

    const getSubmissionData = (): LoanApplication | PrequalApplication => {
        if (usePrequalSubmission) {
            const effectivePrequalId = prequalId ?? setupData?.prequalificationId ?? '';

            return preparePrequalificationData({
                applicationData,
                prequalId: effectivePrequalId,
                tenantId: setupData!.tenantId,
                buId: setupData!.buId,
                merchantId: undefined,
                planNumbers: selectedPlan?.planNumber ? [selectedPlan.planNumber] : undefined,
                requestedAmount: financedAmount,
                technicianLastName: submittingUser?.lastName ?? undefined,
                technicianPhone: submittingUser?.phone ?? undefined,
                technicianEmail: submittingUser?.email ?? undefined,
                fingerPrint: isGreenSky ? fingerprint : undefined,
                source: applicationData.source as unknown as LoanApplicationSubmitSource,
                hasMailingAddress,
                referrer: setupData?.referrer,
            });
        }

        const selectedApplicantSignature = isGreenSky
            ? disclosureState.applicantSignature
            : dynamicDisclosureState.base64Signature;
        const { disclosureToken } = dynamicDisclosureState;
        const { coApplicantSignature } = disclosureState;

        const effectivePrequalId = prequalId ?? setupData?.prequalificationId ?? null;

        const loanApplication = prepareApplicationData({
            applicationData,
            selectedPlan: selectedPlan!,
            saleAmount,
            downPayment,
            financedAmount,
            tenantId: setupData!.tenantId,
            providerName: setupData!.provider,
            buId: setupData!.buId,
            technicianId: setupData!.technicianId,
            customerId: setupData!.customerId,
            jobId: setupData!.jobId,
            fingerPrint: isGreenSky ? fingerprint : undefined,
            applicantSignature: selectedApplicantSignature ?? undefined,
            coApplicantSignature: coApplicantSignature ?? undefined,
            disclosureToken: disclosureToken ?? undefined,
            submittingUser: submittingUser ?? undefined,
            hasMailingAddress,
            referrer: setupData?.referrer,
        }) as LoanApplication;

        // Include prequalId for prequalification flows (e.g., GoodLeap prequalification)
        if (effectivePrequalId) {
            loanApplication.prequalId = effectivePrequalId;
            // Set buId to "Pre-qualification" when submitting prequalification applications
            loanApplication.buId = 'Pre-qualification';
        }

        // Set appliedOn timestamp (required field for LoanApplication)
        loanApplication.appliedOn = new Date().toISOString();

        // Ensure technicianId is always included (required for GoodLeap prequalification)
        loanApplication.technicianId = setupData?.technicianId ?? null;

        return loanApplication;
    };

    const mutation = useMutation<
        { data: SubmitApplicationResult | SubmitPrequalResponseDto },
        ServerError,
        LoanApplication | PrequalApplication
    >({
        mutationKey: usePrequalSubmission
            ? ['submitPrequal', setupData?.customerId, setupData?.jobId]
            : ['submitLoan'],
        mutationFn: data => {
            if (usePrequalSubmission) {
                return prequalsApi.prequalificationsSubmitWithoutLink(
                    {
                        providerName: setupData?.provider,
                        customerId: setupData?.customerId ?? null,
                        jobId: setupData?.jobId ?? null,
                    },
                    data as PrequalApplication,
                );
            }

            return financingApi.loansSubmitApplication22(data as LoanApplication);
        },
        onSuccess: data => {
            hasSubmittedRef.current = true;

            if (usePrequalSubmission) {
                // Store prequalId in SetupDataStore for hard app transition
                const prequalResponse = data.data as SubmitPrequalResponseDto;
                // Set the prequal response in the PrequalificationResponseStore
                // which we will later copy to the SetupDataStore for hard app transition
                if (prequalResponse.prequalId) {
                    setPrequalificationResponse(prequalResponse);
                }

                // Prequal submissions don't return full decision data
                if (onSuccess) {
                    onSuccess({ data: data.data as SubmitPrequalResponseDto });
                }
            } else {
                setDecisionData(data.data as SubmitApplicationResult);
                if (onSuccess) {
                    onSuccess({ data: data.data as SubmitApplicationResult });
                }
            }
        },
        onError: (error: ServerError) => {
            // Reset submission state on error to allow retry
            hasSubmittedRef.current = false;

            if (onError) {
                onError(error);
            }
        },
    });

    const submitLoan = () => {
        if (!enabled) {
            return;
        }
        if (hasSubmittedRef.current) {
            return;
        }

        // For GreenSky, wait for fingerprint before submitting
        if (isGreenSky && !fingerprint) {
            return;
        }

        // For financing API, ensure selectedPlan exists before submitting
        if (!usePrequalSubmission && !selectedPlan) {
            return;
        }

        try {
            const submissionData = getSubmissionData();
            mutation.mutate(submissionData);
        } catch (error) {
            // Error preparing submission data - let mutation handle it
            if (onError) {
                onError(
                    error instanceof Error ? error : new Error('Error preparing submission data'),
                );
            }
        }
    };

    const reset = () => {
        hasSubmittedRef.current = false;
        mutation.reset();
        // Reset persisted fingerprint for GreenSky to allow fresh capture on retry
        if (isGreenSky) {
            persistedFingerprint = undefined;
        }
    };

    useEffect(() => {
        if (enabled && autoSubmit && !hasSubmittedRef.current) {
            // For GreenSky, wait for fingerprint before submitting
            if (isGreenSky && !fingerprint) {
                return;
            }
            submitLoan();
        }

        return () => {
            hasSubmittedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, autoSubmit, isGreenSky, fingerprint]);

    const responseData = mutation.data?.data;
    const prequalIdFromResponse =
        usePrequalSubmission && responseData
            ? ((responseData as SubmitPrequalResponseDto).prequalId ?? null)
            : null;
    const loanIdFromResponse =
        !usePrequalSubmission && responseData
            ? ((responseData as SubmitApplicationResult).loanId ?? null)
            : null;

    return {
        submitLoan,
        isSubmitting: mutation.isPending,
        isSuccess: mutation.isSuccess,
        error: mutation.error ?? null,
        isGreenSky,
        fingerprint,
        reset,
        prequalId: prequalIdFromResponse,
        loanId: loanIdFromResponse,
    };
};
